import React, { useState, useEffect, useCallback } from 'react';
import { Button, Divider, Modal, notification } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import websocketService from './websocketService';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { Package, ShoppingCart, Utensils, ChefHat } from 'lucide-react';
import endPoints from 'utils/endPoints';
import { BAR_QUERY_KEYS } from 'queries/bar/bar';

import urls from 'routes/urls';
import { clearAuthSession } from 'utils/auth';

export interface NotificationData {
  id: string;
  type: string;
  title: string;
  message: string;
  target?: string;
  timestamp: string;
  icon?: string;
  read?: boolean;
  shop_id?: string | number;
  branch_id?: string | number;
  business_type?: string;
}

interface WebSocketComponentProps {
  onNewNotification?: (data: NotificationData) => void;
}

const RESTAURANT_BUSINESS_TYPES = ['restaurant', 'bar', 'cafe', 'coffee_shop', 'coffee shop', 'bakery', 'hotel', 'nightclub'];

const WebSocketComponent: React.FC<WebSocketComponentProps> = ({ onNewNotification }) => {
  const { translate } = useLanguage();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem('accessToken') || '';
    if (!token) return;

    const activeShopId = localStorage.getItem('shop_id') || localStorage.getItem('active_shop_id') || '';
    const activeBranchId = localStorage.getItem('active_branch_id') || '';
    const businessType = (localStorage.getItem('business_type') || localStorage.getItem('shop_business_type') || 'retail').toLowerCase();

    try {
      websocketService.connect(token, '/notification');

      const subscribe = () => {
        if (websocketService.isConnected()) {
          websocketService.send({
            type: 'subscribe',
            data: {
              shop_id: activeShopId,
              branch_id: activeBranchId,
              business_type: businessType,
              features: [
                'token_expiration', 
                'inventory_alerts', 
                'sales_updates',
                'kitchen_updates',
                'waiter_alerts',
                'restaurant_updates',
                'pharmacy_updates',
                'prescription_alerts',
                'medication_alerts'
              ],
              clientMetadata: { timestamp: new Date().toISOString() },
            },
          });
        }
      };

      const handleSocketOpen = () => {
        console.log('WebSocket opened, sending subscription...');
        subscribe();
      };

      if (websocketService.isConnected()) {
        subscribe();
      }

      const handleSocketMessage = (event: MessageEvent) => {
        try {
          const messageData = JSON.parse(event.data);

          // Multi-Tenant Isolation Safeguard
          if (messageData.shop_id && activeShopId && String(messageData.shop_id) !== String(activeShopId)) {
            console.log(`[WebSocket] Ignored notification from shop_id ${messageData.shop_id} (active shop: ${activeShopId})`);
            return;
          }
          if (messageData.branch_id && activeBranchId && String(messageData.branch_id) !== String(activeBranchId)) {
            console.log(`[WebSocket] Ignored notification from branch_id ${messageData.branch_id} (active branch: ${activeBranchId})`);
            return;
          }
          if (messageData.business_type && businessType && String(messageData.business_type).toLowerCase() !== businessType) {
            console.log(`[WebSocket] Ignored notification from business_type ${messageData.business_type} (active business_type: ${businessType})`);
            return;
          }

          // Business Type Specific Gating Safeguard
          const isRestaurantNotif = ['NEW_KITCHEN_ORDER', 'KITCHEN_ORDER_READY', 'WAITER_ALERT'].includes(messageData.type);
          const isPharmacyNotif = ['NEW_PRESCRIPTION', 'PRESCRIPTION_UPDATED', 'MEDICATION_EXPIRY_WARNING'].includes(messageData.type);
          
          if (isRestaurantNotif && !RESTAURANT_BUSINESS_TYPES.includes(businessType)) {
            console.log(`[WebSocket] Ignored restaurant notification ${messageData.type} for business_type ${businessType}`);
            return;
          }
          if (isPharmacyNotif && businessType !== 'pharmacy') {
            console.log(`[WebSocket] Ignored pharmacy notification ${messageData.type} for business_type ${businessType}`);
            return;
          }
          
          let target = '';
          let iconType = '';

          switch (messageData.type) {
            case 'token_expiry_warning':
              setIsModalVisible(true);
              localStorage.setItem('userMustLogin', 'YES');
              return;

            case 'LOW_STOCK':
              queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.lowStockAlerts });
              queryClient.invalidateQueries({ queryKey: ['inventory'] });
              queryClient.invalidateQueries({ queryKey: [endPoints.fetchAllProducts] });
              target = urls.barStock;
              iconType = 'package';
              notification.warning({
                message: translate('Low Stock Alert'),
                description: messageData.message,
                icon: <Package className="text-orange-500" />,
              });
              break;

            case 'NEW_SALE':
              queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
              queryClient.invalidateQueries({ queryKey: ['bar-comptoir-stats'] });
              queryClient.invalidateQueries({ queryKey: [endPoints.fetchAllSales] });
              target = urls.barPOS;
              iconType = messageData.data?.type === 'GUEST_ORDER' ? 'utensils' : 'shopping-cart';
              notification.success({
                message: messageData.data?.type === 'GUEST_ORDER' ? translate('New Guest Order') : translate('New Sale Received'),
                description: messageData.message,
                icon: messageData.data?.type === 'GUEST_ORDER' ? <Utensils className="text-purple-500" /> : <ShoppingCart className="text-green-500" />,
              });
              break;

            case 'NEW_KITCHEN_ORDER':
              queryClient.invalidateQueries({ queryKey: [endPoints.restaurantKitchenTickets] });
              queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
              window.dispatchEvent(new CustomEvent('fuko:kds-update'));
              target = urls.restaurantKitchen;
              iconType = 'chef-hat';
              notification.info({
                message: translate('New Kitchen Order'),
                description: messageData.message,
                icon: <ChefHat className="text-purple-500" />,
              });
              break;

            case 'KITCHEN_ORDER_READY':
              queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
              queryClient.invalidateQueries({ queryKey: [endPoints.restaurantKitchenTickets] });
              window.dispatchEvent(new CustomEvent('fuko:kds-update'));
              target = urls.restaurantTables;
              iconType = 'utensils';
              notification.info({
                message: translate('Order Ready'),
                description: messageData.message,
                icon: <Utensils className="text-blue-500" />,
                duration: 0,
              });
              break;

            case 'NEW_PRESCRIPTION':
            case 'PRESCRIPTION_UPDATED':
              queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
              queryClient.invalidateQueries({ queryKey: ['patients'] });
              target = '/pharmacy/prescriptions';
              iconType = 'package';
              notification.info({
                message: translate('New Prescription Alert'),
                description: messageData.message || translate('A new prescription has been recorded.'),
                icon: <Package className="text-emerald-500" />,
              });
              break;

            case 'MEDICATION_EXPIRY_WARNING':
              queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
              target = '/pharmacy/batches';
              iconType = 'package';
              notification.warning({
                message: translate('Medication Expiry Warning'),
                description: messageData.message || translate('Some medication batches are near expiry.'),
                icon: <Package className="text-red-500" />,
              });
              break;

            default:
              console.log('Received WebSocket Message:', messageData);
              return;
          }

          // Trigger manual callback if provided with rich data
          onNewNotification?.({
            id: Date.now().toString(),
            type: messageData.type,
            title: messageData.type.replace(/_/g, ' '),
            message: messageData.message,
            target,
            icon: iconType,
            timestamp: new Date().toISOString(),
            shop_id: messageData.shop_id || activeShopId,
            branch_id: messageData.branch_id || activeBranchId,
            business_type: messageData.business_type || businessType,
          });

        } catch (error) {
          console.error('Error handling WebSocket message:', error);
        }
      };

      const socketInstance = websocketService.getSocket();
      if (socketInstance) {
        socketInstance.addEventListener('message', handleSocketMessage);
        socketInstance.addEventListener('open', handleSocketOpen);
      }

      return () => {
        websocketService.close();
        if (socketInstance) {
          socketInstance.removeEventListener('message', handleSocketMessage);
          socketInstance.removeEventListener('open', handleSocketOpen);
        }
      };
    } catch (error) {
      console.error('WebSocket Setup Error:', error);
    }
  }, [translate]);

  const handleOpenModal = useCallback(() => {
    const currentUserMustLogin = localStorage.getItem('userMustLogin');
    const freshLogin = localStorage.getItem('newLogin');
    if (currentUserMustLogin === 'YES' && freshLogin !== 'YES') {
      setIsModalVisible(true);
    }
  }, []);

  const handleModalOk = () => {
    clearAuthSession();
    setIsModalVisible(false);
    window.location.href = '/login';
  };

  useEffect(() => {
    handleOpenModal();
  }, [handleOpenModal]);

  return (
    <Modal
      title={translate('Session Expired')}
      open={isModalVisible}
      closeIcon={false}
      footer={
        <Button type="primary" onClick={handleModalOk}>
          {translate('Log In')}
        </Button>
      }
    >
      <Divider />
      <p className="medium-text">
        {translate('Your session has expired. Please log in again to continue')}
      </p>
      <Divider />
    </Modal>
  );
};

export default WebSocketComponent;
export { websocketService };
